// src/pages/MdxServerPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "@/api/apiCall";
import * as yup from "yup";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Server, Loader2, Eye, EyeOff, SquarePen } from "lucide-react";
import DataTable from "@/components/common/DataTable";
import { usePermissions } from "@/context/PermissionContext";

const serverSchema = yup.object().shape({
  server_name: yup.string().required("Instance name is required").min(2).max(50),
  base_url: yup.string().required("Base URL is required"),
  port: yup.string().required("Port is required").matches(/^[0-9]+$/, "Port must be a number")
    .test("port-range", "Port must be between 1 and 65535", (v) => { const p = parseInt(v); return p >= 1 && p <= 65535; }),
  username: yup.string().required("Username is required").min(3).max(30),
  password: yup.string().nullable(),
  is_active: yup.string().oneOf(["Y", "N"]),
});

const MdxServerPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // ── State ──────────────────────────────────────────────────────────────────
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({});
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ server_name: "", base_url: "", port: "", username: "", password: "", is_active: "Y" });
  const hasFetchedRef = useRef(false);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await apiCall("GET", "/get-all-mdx-servers", null, navigate);
      if (response?.success) setServers(response.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchServers();
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const resetForm = () => { setFormData({ server_name: "", base_url: "", port: "", username: "", password: "", is_active: "Y" }); setEditMode(false); setSelectedServerId(null); setErrors({}); setShowPassword(false); };

  const handleDialogOpen = (server = null) => {
    if (server) { setEditMode(true); setSelectedServerId(server.id); setFormData({ server_name: server.server_name, base_url: server.base_url, port: server.port, username: server.username, password: server.decrypt_password, is_active: server.is_active }); }
    else resetForm();
    setDialogOpen(true);
  };

  const handleDialogClose = () => { setDialogOpen(false); setTimeout(resetForm, 200); };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    try {
      await serverSchema.validate(formData, { abortEarly: false });
      setSubmitting(true);
      const payload = { ...formData };
      if (editMode) { payload.id = selectedServerId; await apiCall("PUT", "/update-mdx-server", payload, navigate); }
      else await apiCall("POST", "/create-mdx-server", payload, navigate);
      handleDialogClose();
      fetchServers();
    } catch (err) {
      if (err.name === "ValidationError") { const e = {}; err.inner.forEach((er) => { e[er.path] = er.message; }); setErrors(e); }
    } finally { setSubmitting(false); }
  };

  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      await apiCall("DELETE", `/delete-mdx-server/${selectedServerId}`, null, navigate);
      setDeleteDialogOpen(false);
      fetchServers();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); setSelectedServerId(null); }
  };

  // ── DataTable config ───────────────────────────────────────────────────────
  const columns = [
    {
      key: "server_name",
      label: "Instance Name",
      render: (row) => <div className="font-medium">{row.server_name}</div>,
    },
    { key: "base_url", label: "Base URL" },
    { key: "port", label: "Port" },
    { key: "username", label: "Username" },
    {
      key: "decrypt_password",
      label: "Password",
      render: (row) =>
        row.decrypt_password === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{passwordVisibility[row.id] ? row.decrypt_password : "••••••••"}</span>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setPasswordVisibility((p) => ({ ...p, [row.id]: !p[row.id] }))}>
              {passwordVisibility[row.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
          </div>
        ),
    },
    {
      key: "is_active",
      label: "Status",
      render: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${row.is_active === "Y" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"}`}>
          {row.is_active === "Y" ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const rowActions = (row) => [
    // { label: "Edit", icon: <SquarePen className="h-4 w-4" />, onClick: () => handleDialogOpen(row), hidden: !hasPermission("instance_edit") },
    {
        label: "Edit",
        icon: (
          <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#DBFCE780] text-[#407E3B] dark:bg-[#DBFCE715] dark:text-[#6DBF67]">
            <SquarePen className="h-4 w-4" />
          </span>
        ),
        onClick: () => handleDialogOpen(row),
        hidden: !hasPermission("instance_edit"),
      },
    // { label: "Delete", icon: <Trash2 className="h-4 w-4" />, onClick: () => { setSelectedServerId(row.id); setDeleteDialogOpen(true); }, className: "text-destructive focus:text-destructive", hidden: !hasPermission("instance_delete") },
     {
        label: "Delete",
        icon: (
          <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#FAE6EC80] text-[#D3663D] dark:bg-[#FAE6EC15] dark:text-[#F28B6E">
            <Trash2 className="h-4 w-4" />
          </span>
        ),
        onClick: () => { setSelectedServerId(row.id); setDeleteDialogOpen(true); },
        className: "text-destructive focus:text-destructive",
        hidden: !hasPermission("instance_delete"),
      },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <DataTable
        title="Instance Management"
        description="Manage your instance configurations and credentials"
        // icon={<Server className="h-5 w-5" />}
        headerActions={
          hasPermission("instance_create") && (
            <Button onClick={() => handleDialogOpen()} className="gap-2 w-full sm:w-auto h-9 text-sm">
              <Plus className="h-4 w-4" /> Add Instance
            </Button>
          )
        }
        showSearch={false}
        columns={columns}
        rows={servers}
        rowKey="id"
        showPagination={false}
        loading={loading}
        emptyIcon={<Server className="h-12 w-12 text-muted-foreground/40" />}
        emptyMessage="No instance found. Add your first instance to get started."
        rowActions={rowActions}
      />

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="w-[95vw] max-w-[560px] max-h-[90vh] overflow-y-auto lg:min-w-[700px] lg:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Instance" : "Add New Instance"}</DialogTitle>
            <DialogDescription>
              {editMode ? "Update the instance configuration details below." : "Enter the details for the new instance configuration."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor="server_name">Instance Name <span className="text-destructive">*</span></Label>
                <Input id="server_name" name="server_name" value={formData.server_name} onChange={handleInputChange} placeholder="e.g., KPI Server" className={errors.server_name ? "border-destructive" : ""} autoComplete="off" />
                {errors.server_name && <p className="text-xs text-destructive">{errors.server_name}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="base_url">Base URL <span className="text-destructive">*</span></Label>
                <Input id="base_url" name="base_url" value={formData.base_url} onChange={handleInputChange} placeholder="e.g., www.example.com" className={errors.base_url ? "border-destructive" : ""} autoComplete="off" />
                {errors.base_url && <p className="text-xs text-destructive">{errors.base_url}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="port">Port <span className="text-destructive">*</span></Label>
                <Input id="port" name="port" value={formData.port} onChange={handleInputChange} placeholder="e.g., 5000" className={errors.port ? "border-destructive" : ""}  autoComplete="off" />
                {errors.port && <p className="text-xs text-destructive">{errors.port}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="username">Username <span className="text-destructive">*</span></Label>
                <Input id="username" name="username" value={formData.username} onChange={handleInputChange} placeholder="e.g., Admin" className={errors.username ? "border-destructive" : ""}  autoComplete="off" />
                {errors.username && <p className="text-xs text-destructive">{errors.username}</p>}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={handleInputChange} placeholder="Enter password" className={`pr-10 ${errors.password ? "border-destructive" : ""}`}  autoComplete="off" />
                  <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none" tabIndex={-1}>
                    {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {editMode && (
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label>Status</Label>
                  <div className="flex items-center gap-6">
                    {["Y", "N"].map((val) => (
                      <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="radio" name="is_active" value={val} checked={formData.is_active === val} onChange={handleInputChange} className="h-4 w-4" />
                        <span>{val === "Y" ? "Active" : "Inactive"}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={handleDialogClose} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{editMode ? "Updating..." : "Adding..."}</> : editMode ? "Update Instance" : "Add Instance"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="w-[95vw] max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. This will permanently delete the instance configuration.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto" onClick={() => setSelectedServerId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={submitting} className="bg-destructive text-muted hover:bg-destructive/90 w-full sm:w-auto">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : "Delete Instance"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MdxServerPage;